import React from 'react';
import { Scale, FileText, AlertTriangle, CheckCircle, Clock, DollarSign } from 'lucide-react';

export const TermsOfService = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-orange-100 p-4 rounded-full">
              <Scale size={48} className="text-orange-600" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Terms of Service
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            These terms govern the use of our website and construction services.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: October 2025
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Acceptance of Terms */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <CheckCircle size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                By accessing and using the Masonry Homes LLC website ("Website") or engaging our construction 
                services ("Services"), you accept and agree to be bound by the terms and provision of this agreement.
              </p>
              <p className="text-gray-700 mb-4">
                If you do not agree to abide by the above, please do not use this Website or our Services.
              </p>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                <h4 className="font-semibold text-orange-800 mb-2">Important Notice</h4>
                <p className="text-orange-700 text-sm">
                  These Terms of Service apply to all visitors, users, and others who access or use our Website 
                  and Services. By using our Website, you represent that you are at least 18 years old.
                </p>
              </div>
            </div>
          </div>

          {/* Website Use */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">2. Website Use and Restrictions</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Permitted Use</h3>
              <p className="text-gray-700 mb-4">
                You may use our Website to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Browse our services and project portfolios</li>
                <li>Request quotes and consultations</li>
                <li>Contact us for construction services</li>
                <li>Learn about our company and capabilities</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Prohibited Activities</h3>
              <p className="text-gray-700 mb-4">
                You agree NOT to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Use the Website for any unlawful purpose or activity</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Transmit viruses, malware, or other harmful code</li>
                <li>Copy, distribute, or modify our content without permission</li>
                <li>Use automated systems to scrape or harvest data</li>
                <li>Impersonate Masonry Homes LLC or our employees</li>
                <li>Submit false or misleading information in forms</li>
              </ul>
            </div>
          </div>

          {/* Construction Services Terms */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <DollarSign size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">3. Construction Services Terms</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Service Agreements</h3>
              <p className="text-gray-700 mb-4">
                All construction services are subject to separate written contracts that will include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Detailed project specifications and scope of work</li>
                <li>Project timeline and completion dates</li>
                <li>Payment terms and schedule</li>
                <li>Materials and labor specifications</li>
                <li>Change order procedures</li>
                <li>Warranty terms and conditions</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Estimates and Quotes</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Initial estimates are preliminary and subject to change upon detailed assessment</li>
                <li>Written quotes are valid for 30 days unless otherwise specified</li>
                <li>Final pricing may vary based on site conditions, material costs, and project modifications</li>
                <li>All quotes exclude permits, utility connections, and unforeseen conditions unless specified</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Terms</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Payment schedules will be outlined in individual contracts</li>
                <li>We may require deposits or progress payments as work proceeds</li>
                <li>Final payment is due upon project completion and client acceptance</li>
                <li>Late payments may incur additional fees as specified in contracts</li>
              </ul>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">4. Intellectual Property Rights</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                All content on this Website, including but not limited to text, graphics, logos, images, 
                photographs, and software, is the property of Masonry Homes LLC or its licensors and is 
                protected by copyright, trademark, and other intellectual property laws.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Project Photography</h3>
              <p className="text-gray-700 mb-4">
                By engaging our services, you grant Masonry Homes LLC the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Photograph completed projects for our portfolio</li>
                <li>Use project images in marketing materials and website</li>
                <li>Display before and after photos of renovation work</li>
                <li>Feature projects in case studies and testimonials (with your consent)</li>
              </ul>
              
              <p className="text-gray-700">
                We will respect your privacy and will not include identifying information without your explicit permission.
              </p>
            </div>
          </div>

          {/* Liability and Warranties */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <AlertTriangle size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">5. Liability and Warranties</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Construction Warranties</h3>
              <p className="text-gray-700 mb-4">
                Masonry Homes LLC provides warranties on our construction work as follows:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li><strong>Workmanship:</strong> 1-2 years on labor and installation (varies by project type)</li>
                <li><strong>Materials:</strong> Subject to manufacturer warranties</li>
                <li><strong>Structural Work:</strong> Extended warranties as required by local building codes</li>
                <li><strong>Excluded Items:</strong> Normal wear and tear, damage from misuse, or acts of nature</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Website Disclaimer</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <p className="text-yellow-800 text-sm">
                  <strong>IMPORTANT:</strong> This Website and its contents are provided "as is" without warranties 
                  of any kind. We do not guarantee that the Website will be error-free, uninterrupted, or free 
                  from viruses or other harmful components.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Limitation of Liability</h3>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, Masonry Homes LLC shall not be liable for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Indirect, incidental, or consequential damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Damages exceeding the amount paid for services</li>
                <li>Delays due to weather, material shortages, or permit issues</li>
              </ul>
            </div>
          </div>

          {/* Insurance and Licensing */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <CheckCircle size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">6. Insurance and Licensing</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                Masonry Homes LLC maintains appropriate licensing and insurance coverage:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li><strong>Contractor License:</strong> Valid general contractor license in our operating jurisdiction</li>
                <li><strong>Liability Insurance:</strong> General liability coverage for property damage and injuries</li>
                <li><strong>Workers' Compensation:</strong> Coverage for all employees and subcontractors</li>
                <li><strong>Bonding:</strong> Surety bonds as required by local regulations</li>
              </ul>
              
              <p className="text-gray-700">
                Proof of insurance and licensing can be provided upon request before project commencement.
              </p>
            </div>
          </div>

          {/* Privacy and Data Collection */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">7. Privacy and Data Collection</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                Your privacy is important to us. Our collection and use of personal information is governed 
                by our Privacy Policy, which is incorporated into these Terms of Service by reference.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Form Data</h3>
              <p className="text-gray-700 mb-4">
                Information submitted through our contact forms will be used to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Respond to your inquiries and service requests</li>
                <li>Prepare quotes and project proposals</li>
                <li>Schedule consultations and site visits</li>
                <li>Maintain records of our business relationship</li>
              </ul>
            </div>
          </div>

          {/* Dispute Resolution */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Scale size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">8. Dispute Resolution</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Informal Resolution</h3>
              <p className="text-gray-700 mb-4">
                We encourage clients to contact us directly to resolve any concerns or disputes informally. 
                Most issues can be resolved through open communication and good faith efforts.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Formal Dispute Process</h3>
              <p className="text-gray-700 mb-4">
                If informal resolution is not possible, disputes will be handled as follows:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li><strong>Mediation:</strong> First attempt at formal resolution through a neutral mediator</li>
                <li><strong>Arbitration:</strong> Binding arbitration if mediation is unsuccessful</li>
                <li><strong>Governing Law:</strong> Disputes governed by the laws of our business jurisdiction</li>
                <li><strong>Venue:</strong> Legal proceedings in the courts of our business location</li>
              </ul>
            </div>
          </div>

          {/* Termination */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Clock size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">9. Termination</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                These Terms of Service remain in effect while you use our Website or Services. We may 
                terminate or suspend access immediately, without prior notice, for any breach of these Terms.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Construction Contract Termination</h3>
              <p className="text-gray-700 mb-4">
                Construction service agreements may be terminated under conditions specified in individual contracts, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Mutual agreement between parties</li>
                <li>Material breach of contract terms</li>
                <li>Non-payment after specified grace periods</li>
                <li>Changes in project scope beyond original agreement</li>
              </ul>
            </div>
          </div>

          {/* Changes to Terms */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">10. Changes to Terms</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective 
                when posted on our Website with an updated "Last updated" date.
              </p>
              <p className="text-gray-700 mb-4">
                Continued use of our Website or Services after changes are posted constitutes acceptance 
                of the revised terms.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-blue-800 mb-2">Notification of Changes</h4>
                <p className="text-blue-700 text-sm">
                  For significant changes to these terms, we will make reasonable efforts to notify 
                  active clients via email or through prominent website notices.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">11. Contact Information</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="space-y-2 text-gray-700">
                  <p><strong>Masonry Homes LLC</strong></p>
                  <p>📧 Email: masonryhomes@yahoo.com</p>
                  <p>📞 Phone: (847) 401-5381</p>
                  <p>🕒 Business Hours: Monday-Friday 8AM-6PM, Saturday 8AM-6PM</p>
                </div>
              </div>
            </div>
          </div>

          
        </div>
      </section>
    </div>
  );
};